import { isSupabaseReady } from '../services/supabase.js';
import { t } from '../services/i18n.js';
import QRCode from 'qrcode';
import {
  downloadCurrentKeycard,
  exportCurrentKeycard,
  importKeycard,
  readTextFile,
  signInWithAnonymousKeycard
} from '../services/keycard-auth.js';

export function renderLogin(container) {
  const missingConfig = !isSupabaseReady();

  container.innerHTML = `
    <div class="page app-shell">

      <!-- Header -->
      <header class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">Fooview</p>
            <p class="brand-subtitle">${t('brandSubtitle')}</p>
          </div>
        </div>
      </header>

      <!-- Hero -->
      <section class="hero-panel">
        <div class="hero-copy">
          <div>
            <p class="eyebrow">${t('loginEyebrow')}</p>
            <h1 class="heading-1">${t('loginTitle')}</h1>
            <p class="lede">${t('loginLead')}</p>
          </div>
          <div>
            <div class="login-actions">
              <div class="keycard-login-panel">
                <p class="eyebrow">Accès anonyme</p>
                <p class="keycard-login-intro">Crée une keycard si tu viens d'arriver. Si tu as déjà un compte, connecte-toi avec ton fichier .fooviewkey ou ton QR.</p>
                <div class="keycard-login-grid">
                  <section class="keycard-login-section" aria-labelledby="create-keycard-title">
                    <h2 id="create-keycard-title" class="keycard-section-title">Nouveau compte</h2>
                    <p class="keycard-section-copy">Choisis un pseudo public, puis sauvegarde la keycard générée.</p>
                    <input id="anonymous-pseudo" class="input-field" type="text" maxlength="28" placeholder="Pseudo public" ${missingConfig ? 'disabled' : ''}>
                    <input id="create-keycard-passphrase" class="input-field" type="password" placeholder="Phrase secrète optionnelle" ${missingConfig ? 'disabled' : ''}>
                    <div class="actions" style="margin-top:10px;">
                      <button id="btn-anonymous-keycard" class="btn-primary" type="button" ${missingConfig ? 'disabled' : ''}>Créer mon accès</button>
                    </div>
                  </section>
                  <section class="keycard-login-section" aria-labelledby="import-keycard-title">
                    <h2 id="import-keycard-title" class="keycard-section-title">Déjà un compte</h2>
                    <p class="keycard-section-copy">Importe ton fichier .fooviewkey, colle le contenu de ta keycard ou sélectionne une image QR.</p>
                    <input id="import-keycard-passphrase" class="input-field" type="password" placeholder="Phrase secrète si ta keycard en a une" ${missingConfig ? 'disabled' : ''}>
                    <textarea id="keycard-text" class="input-field keycard-textarea" placeholder="Ou colle ici le contenu de ta keycard" ${missingConfig ? 'disabled' : ''}></textarea>
                    <input id="keycard-file" class="keycard-file-input" type="file" accept=".fooviewkey,text/plain,image/*" ${missingConfig ? 'disabled' : ''}>
                    <button id="btn-import-keycard" class="btn-primary" type="button" style="width:100%;" ${missingConfig ? 'disabled' : ''}>Se connecter</button>
                    <button id="btn-read-keycard-qr" class="btn-ghost keycard-qr-helper" type="button" ${missingConfig ? 'disabled' : ''}>Décoder l'image QR sélectionnée</button>
                  </section>
                </div>
                <div id="keycard-output"></div>
                <p class="text-muted" style="font-size:12px;margin-top:10px;">La phrase secrète est optionnelle. Sans elle, le fichier/QR donne accès direct à la session; avec elle, la keycard est chiffrée.</p>
              </div>
            </div>
            ${missingConfig
              ? `<p class="text-muted" style="color:#c0392b; font-size:12px; margin-top:12px;">${t('supabaseMissing')}</p>`
              : ''
            }
          </div>
        </div>

        <!-- Pitch Canvas -->
        <div class="hero-field" aria-hidden="true">
          <canvas id="login-pitch-canvas" style="position:absolute; inset:0; width:100%; height:100%;"></canvas>
          <div class="field-card">
            <strong>${t('tacticalSimulation')}</strong>
            <span>${t('tacticalSimulationCopy')}</span>
          </div>
        </div>
      </section>

    </div>
  `;

  const canvas = container.querySelector('#login-pitch-canvas');
  if (canvas) {
    const ctx = canvas.getContext('2d');
    let raf;
    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      canvas.width = r.width;
      canvas.height = r.height;
    };
    resize();
    window.addEventListener('resize', resize);

    const fieldColor = (name, fallback) => {
      const value = getComputedStyle(canvas.parentElement).getPropertyValue(name).trim();
      return value || fallback;
    };

    let t = 0;
    const line = (x1, y1, x2, y2, color) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    };

    const dot = (x, y, r, color, glow = 0) => {
      ctx.save();
      if (glow) { ctx.shadowBlur = glow; ctx.shadowColor = color; }
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    const animate = () => {
      if (!document.body.contains(canvas)) {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', resize);
        return;
      }
      t += 0.008;
      const w = canvas.width, h = canvas.height;
      const bgInner = fieldColor('--pitch-bg-inner', '#0d1c11');
      const bgOuter = fieldColor('--pitch-bg-outer', '#060c08');
      const pitchLine = fieldColor('--pitch-line', 'rgba(255,255,255,0.08)');
      const tacticLine = fieldColor('--pitch-tactic-line', 'rgba(26,122,74,0.25)');
      const attack = fieldColor('--pitch-player-attack', '#1a7a4a');
      const defense = fieldColor('--pitch-player-defense', '#7a1a1a');
      const stroke = fieldColor('--pitch-player-stroke', 'rgba(255,255,255,0.4)');
      const ball = fieldColor('--pitch-ball', '#fff');

      ctx.clearRect(0, 0, w, h);
      const g = ctx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, Math.max(w, h));
      g.addColorStop(0, bgInner);
      g.addColorStop(1, bgOuter);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      const m = 18;
      ctx.strokeStyle = pitchLine;
      ctx.lineWidth = 1;
      ctx.strokeRect(m, m, w - m * 2, h - m * 2);
      line(w / 2, m, w / 2, h - m, pitchLine);
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, Math.min(w, h) * 0.16, 0, Math.PI * 2);
      ctx.stroke();

      const boxW = w * 0.16, boxH = h * 0.38;
      ctx.strokeRect(m, (h - boxH) / 2, boxW, boxH);
      ctx.strokeRect(w - m - boxW, (h - boxH) / 2, boxW, boxH);

      const p = [
        { x: w * 0.30 + Math.sin(t) * 18, y: h * 0.38 + Math.cos(t * 0.9) * 12 },
        { x: w * 0.28 + Math.cos(t * 1.1) * 14, y: h * 0.62 + Math.sin(t * 0.8) * 14 },
        { x: w * 0.44 + Math.sin(t * 0.7) * 22, y: h * 0.50 + Math.cos(t * 0.6) * 18 }
      ];
      const q = [
        { x: w * 0.62 + Math.cos(t * 0.85) * 14, y: h * 0.40 + Math.sin(t * 1.1) * 12 },
        { x: w * 0.65 + Math.sin(t * 0.7) * 16, y: h * 0.60 + Math.cos(t * 0.9) * 14 }
      ];

      ctx.setLineDash([4, 5]);
      ctx.strokeStyle = tacticLine;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(p[0].x, p[0].y);
      ctx.lineTo(p[2].x, p[2].y);
      ctx.lineTo(p[1].x, p[1].y);
      ctx.stroke();
      ctx.setLineDash([]);

      const ballWeight = (Math.sin(t * 1.8) + 1) / 2;
      const bx = p[2].x * ballWeight + q[0].x * (1 - ballWeight);
      const by = p[2].y * ballWeight + q[0].y * (1 - ballWeight);

      p.forEach(({ x, y }) => {
        dot(x, y, 7, attack, 10);
        ctx.save();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();
      });
      q.forEach(({ x, y }) => {
        dot(x, y, 7, defense, 8);
        ctx.save();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.stroke();
        ctx.restore();
      });
      dot(bx, by, 4.5, ball, 12);

      raf = requestAnimationFrame(animate);
    };
    animate();
  }

  // Events
  if (!missingConfig) {
    container.querySelector('#btn-anonymous-keycard').addEventListener('click', async () => {
      try {
        const passphrase = container.querySelector('#create-keycard-passphrase').value;
        await signInWithAnonymousKeycard(container.querySelector('#anonymous-pseudo').value);
        const keycard = await exportCurrentKeycard(passphrase);
        const qr = await QRCode.toDataURL(keycard, { errorCorrectionLevel: 'M', margin: 2, width: 260 });
        container.querySelector('#keycard-output').innerHTML = `
          <div class="keycard-result-card">
            <img src="${qr}" alt="QR keycard Fooview">
            <div>
              <strong>Keycard générée</strong>
              <p class="text-muted">Sauvegarde ce QR ou le fichier .fooviewkey${passphrase ? ' avec ta phrase secrète' : ''}. Sans keycard, tu peux perdre ton accès sur un nouvel appareil.</p>
              <button id="btn-download-keycard-login" class="btn-primary" type="button">Télécharger .fooviewkey</button>
              <a class="btn-secondary" href="#/home">Entrer dans l'app</a>
            </div>
          </div>
        `;
        container.querySelector('#btn-download-keycard-login').addEventListener('click', async () => {
          try { await downloadCurrentKeycard(passphrase); } catch (e) { showToast(e.message); }
        });
      } catch (e) {
        showToast('Keycard impossible : ' + e.message);
      }
    });

    container.querySelector('#btn-import-keycard').addEventListener('click', async () => {
      try {
        const passphrase = container.querySelector('#import-keycard-passphrase').value;
        const serialized = await readKeycardInput(container);
        if (!serialized) throw new Error('Choisis un fichier .fooviewkey, une image QR, ou colle le contenu de ta keycard.');
        await importKeycard(serialized, passphrase);
        window.location.hash = '#/home';
      } catch (e) {
        showToast('Connexion impossible : ' + e.message);
      }
    });

    container.querySelector('#btn-read-keycard-qr').addEventListener('click', async () => {
      try {
        const file = container.querySelector('#keycard-file').files[0];
        if (!file) throw new Error('Sélectionne d’abord une image QR.');
        if (!isImageFile(file)) throw new Error('Ce bouton sert seulement aux images QR. Pour un fichier .fooviewkey, clique directement sur Se connecter.');
        const text = await decodeQrImage(file);
        container.querySelector('#keycard-text').value = text;
        showToast('QR lu. Clique sur Se connecter.');
      } catch (e) {
        showToast('Lecture QR impossible : ' + e.message);
      }
    });
  }

}

async function readKeycardInput(container) {
  const typed = container.querySelector('#keycard-text').value.trim();
  if (typed) return typed;
  const file = container.querySelector('#keycard-file').files[0];
  if (!file) return '';
  if (isImageFile(file)) return decodeQrImage(file);
  return readTextFile(file);
}

function isImageFile(file) {
  return String(file?.type || '').startsWith('image/');
}

async function decodeQrImage(file) {
  if (!('BarcodeDetector' in window)) {
    throw new Error('lecture QR non disponible sur ce navigateur. Utilise le fichier .fooviewkey ou colle le contenu de la keycard.');
  }
  const detector = new BarcodeDetector({ formats: ['qr_code'] });
  const bitmap = await createImageBitmap(file);
  const codes = await detector.detect(bitmap);
  if (!codes[0]?.rawValue) throw new Error('aucun QR détecté.');
  return codes[0].rawValue;
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 3500);
}
